type CopyTextFn = (text: string, onCopied?: () => void, onFailed?: () => void) => void;
type LogCopyFn = ((payload: {
  sourceTextLength: number;
  sourceTextPreview: string;
  sourceTextTail: string;
}) => void) | null;

type ActionMenuOptions = {
  copyText: CopyTextFn;
  text: string;
  resolveText?: ((wrap: HTMLDivElement) => string) | null;
  logCopy?: LogCopyFn;
  onRebuildSession?: (() => void) | null;
  onTruncateSessionForEdit?: (() => void) | null;
};

const COPY_RESET_MS = 1200;

function createButton(label: string, ariaLabel: string): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'msg-meta-btn';
  button.textContent = label;
  button.setAttribute('aria-label', ariaLabel);
  return button;
}

function createItem(label: string, ariaLabel: string, disabled: boolean, onClick: () => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'msg-meta-menu-item';
  button.textContent = label;
  button.setAttribute('aria-label', ariaLabel);
  button.disabled = disabled;
  button.addEventListener('click', onClick);
  return button;
}

function attachMenuRoot(wrap: HTMLDivElement, trigger: HTMLButtonElement, menu: HTMLDivElement): void {
  const closeMenu = () => {
    menu.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
  };

  trigger.addEventListener('click', (event) => {
    event.stopPropagation();
    menu.hidden = !menu.hidden;
    trigger.setAttribute('aria-expanded', menu.hidden ? 'false' : 'true');
  });

  menu.addEventListener('click', (event) => {
    event.stopPropagation();
  });

  document.addEventListener('click', closeMenu, { once: true });

  wrap.appendChild(trigger);
  wrap.appendChild(menu);
}

export function createMessageActionMenu(options: ActionMenuOptions): HTMLDivElement {
  const wrap = document.createElement('div');
  wrap.className = 'msg-meta-menu-wrap';

  const trigger = createButton('⋯', '消息操作菜单');
  trigger.classList.add('msg-meta-menu-trigger');
  trigger.setAttribute('aria-expanded', 'false');

  const menu = document.createElement('div');
  menu.className = 'msg-meta-menu';
  menu.hidden = true;
  attachMenuRoot(wrap, trigger, menu);

  const copyButton = createItem('复制', '复制消息', !(options.text || typeof options.resolveText === 'function'), () => {
    const resolvedText = (typeof options.resolveText === 'function' ? options.resolveText(wrap) : options.text).trim();
    if (!resolvedText) return;
    options.logCopy?.({
      sourceTextLength: resolvedText.length,
      sourceTextPreview: resolvedText.slice(0, 240),
      sourceTextTail: resolvedText.slice(-240),
    });
    const original = copyButton.textContent || '复制';
    options.copyText(resolvedText, () => {
      copyButton.textContent = '已复制';
      window.setTimeout(() => { copyButton.textContent = original; }, COPY_RESET_MS);
    }, () => {
      copyButton.textContent = '复制失败';
      window.setTimeout(() => { copyButton.textContent = original; }, COPY_RESET_MS);
    });
    menu.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
  });

  menu.appendChild(copyButton);
  if (typeof options.onRebuildSession === 'function') {
    const rebuildButton = createItem('重建会话', '重建会话', false, () => {
      options.onRebuildSession?.();
      menu.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
    });
    menu.appendChild(rebuildButton);
  }
  if (typeof options.onTruncateSessionForEdit === 'function') {
    const editButton = createItem('截断会话并编辑', '截断会话并编辑', false, () => {
      options.onTruncateSessionForEdit?.();
      menu.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
    });
    menu.appendChild(editButton);
  }
  return wrap;
}
